import * as React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Favicon from './Favicon';

type SEOProps = {
  description?: string;
  title: string;
  children?: React.ReactNode;
  image?: string;
};

const SEO: React.FC<SEOProps> = ({ description, title, children, image }) => {
  const { site, logoImage } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
          business {
            name
            openingHours
            telephone
            address {
              streetAddress
              addressLocality
              addressRegion
              postalCode
            }
            serviceTypes
          }
          social {
            facebook
            google
          }
        }
      }
      logoImage: file(relativePath: { eq: "logo.png" }) {
        publicURL
      }
    }
  `);

  const [url, setUrl] = React.useState('');

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrl(window.location.href);
    }
  }, []);

  const defaultTitle = site.siteMetadata?.title;

  const metaDescription = description || site.siteMetadata.description;
  const enhancedMetaDescription = description
    ? description
    : `${metaDescription} Contact us today for a free car repair quote!`;

  const fullTitle = defaultTitle ? `${title} | ${defaultTitle}` : title;
  const siteImage = `${site.siteMetadata.siteUrl}/images/logo.png`;
  const defaultImage = logoImage?.publicURL
    ? `${site.siteMetadata.siteUrl}${logoImage.publicURL}`
    : siteImage;

  const imageUrl = image || defaultImage;

  // const imageUrl = image
  //   ? image.startsWith('/')
  //     ? `${site.siteMetadata.siteUrl}${image}` // Prepend siteUrl if it's a root-relative path
  //     : image // Assume it's already an absolute URL
  //   : defaultImageUrl;
  const business = site.siteMetadata.business;

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    name: business.name,
    image: imageUrl,
    description: metaDescription,
    '@id': site.siteMetadata.siteUrl,
    url: site.siteMetadata.siteUrl,
    telephone: business.telephone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: business.address.streetAddress,
      addressLocality: business.address.addressLocality,
      addressRegion: business.address.addressRegion,
      postalCode: business.address.postalCode,
      addressCountry: 'GB',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
    sameAs: [
      site.siteMetadata.social.facebook,
      site.siteMetadata.social.google,
    ],
    priceRange: '££',
    // servesCuisine: business.serviceTypes,
    // Change to service offerings
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Auto Services',
      itemListElement: business.serviceTypes.map(
        (service: string, index: number) => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: service,
          },
        })
      ),
    },
  };

  return (
    <>
      {/* Custom Favicon */}
      <Favicon />
      <html lang="en-GB" />

      {/* Viewport meta tag */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <title>{fullTitle}</title>
      <meta name="description" content={enhancedMetaDescription} />
      {/* Add a robots meta tag */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content={site.siteMetadata.author} />
      <meta
        name="keywords"
        content={`car repair, MOT, servicing, Stoke-on-Trent, ${business.serviceTypes.join(', ')}`}
      />

      {/* Open Graph / Facebook */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={enhancedMetaDescription} />
      <meta property="og:type" content="business.business" />
      {/* <meta property="og:image" content={imageUrl} /> */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={business.name} />
      <meta property="og:locale" content="en_GB" />
      <meta
        property="business:contact_data:street_address"
        content={business.address.streetAddress}
      />
      <meta
        property="business:contact_data:locality"
        content={business.address.addressLocality}
      />
      <meta
        property="business:contact_data:postal_code"
        content={business.address.postalCode}
      />
      <meta
        property="business:contact_data:country_name"
        content="United Kingdom"
      />
      <meta
        property="business:contact_data:phone_number"
        content={business.telephone}
      />
      <meta property="business:hours:day" content={business.openingHours} />

      {/* Twitter */}

      <meta property="fb:app_id" content="1961355237630991" />

      {/* Bing */}
      <meta name="msvalidate.01" content="C98A20491F641F2E86AC0EC35080C750" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={site.siteMetadata?.author || ''} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {/* <meta name="twitter:image" content={imageUrl} /> */}
      {imageUrl && (
        <>
          <meta property="og:image" content={imageUrl} />
          <meta name="twitter:image" content={imageUrl} />
          {/* Preload the default logo if it's the one being used */}
          {imageUrl === defaultImage && (
            <link
              rel="preload"
              as="image"
              href={logoImage.publicURL} // Use the relative publicURL for preloading
              type="image/png" // Adjust type if your logo isn't png
            />
          )}
        </>
      )}

      {/* <meta name="twitter:site" content={site.siteMetadata.author} /> */}

      {/* Schema.org markup for Google */}
      <script defer type="application/ld+json">
        {JSON.stringify(schemaOrg)}
      </script>

      {url && (
        <>
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
        </>
      )}

      <link
        rel="preload"
        as="image"
        href="../images/logo.png"
        type="image/png"
        // @ts-ignore
        fetchpriority="high"
      />
      {/* DNS prefetch and preconnect for Facebook */}
      <link rel="dns-prefetch" href="//www.facebook.com" />
      <link rel="dns-prefetch" href="//connect.facebook.net" />
      <link rel="dns-prefetch" href="//static.xx.fbcdn.net" />

      <link
        rel="preconnect"
        href="//www.facebook.com"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect"
        href="//connect.facebook.net"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect"
        href="//static.xx.fbcdn.net"
        crossOrigin="anonymous"
      />

      <noscript>
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@700&display=swap"
        />
      </noscript>
      {children}
    </>
  );
};

export default SEO;
